import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { fetchExpertReportById } from './api';
import { expertReportKeys } from './hooks';
import type { ExpertReportResponse } from './types';

/** Соответствие `reportId → карточка экспертного заключения` */
export type ExpertReportDetailsMap = Map<number, ExpertReportResponse>;

/**
 * Загружает карточки указанных экспертных заключений и отдаёт `reportId → карточка` вместе
 * с признаком незавершённой загрузки.
 *
 * Нужен там, где требуется содержимое заключения, которого список заключений не несёт, — блоки
 * с их правилами и видом результатов: оно приходит только карточкой. Кэш общий с карточкой одного
 * заключения — повторного запроса при её открытии не будет. Запросов столько же, сколько
 * заключений, поэтому набор стоит откладывать через `enabled` до момента, когда он действительно нужен.
 */
export const useExpertReportsDetails = (
  reportIds: number[],
  options?: { enabled?: boolean },
): { details: ExpertReportDetailsMap; isLoading: boolean } => {
  const enabled = options?.enabled !== false;

  const queries = useQueries({
    queries: reportIds.map((id) => ({
      queryKey: expertReportKeys.detail(id),
      queryFn: () => fetchExpertReportById(id),
      enabled,
      staleTime: 60_000,
    })),
  });

  const isLoading = queries.some((query) => query.isLoading);

  const details = useMemo(() => {
    const map: ExpertReportDetailsMap = new Map();
    queries.forEach((query, index) => {
      if (query.data) map.set(reportIds[index], query.data);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.map((query) => query.dataUpdatedAt).join('|'), reportIds.join(',')]);

  return { details, isLoading };
};
